// ─── Finishing Preview Service ───
// Handles Supabase insert/poll/result with mock fallback
import { supabase } from '@/lib/supabase';
import type {
  CreateJobRequest,
  FinishingPreviewJob,
  FinishingPreviewResult,
  FinishingLine,
} from '@/mocks/finishingPreviewJob';

// ─── Standalone Types (no mock dependency) ───

export interface FinishingOption {
  id: string;
  name: string;
  description: string;
  minOrder: number;
  leadTimeDays: number;
  primaryCategories: string[];
  secondaryCategories: string[];
}

export interface SupplierInfo {
  name: string;
  location: string;
  verified: boolean;
  certifications: string[];
  established: number;
  employeeCount: string;
  exportMarkets: string[];
}

export interface ReviewItem {
  id: string;
  author: string;
  country: string;
  rating: number;
  content: string;
  date: string;
}

export interface ProductDetailData {
  id: string;
  name: string;
  sku: string;
  category: string;
  categoryL1: string;
  categoryL2: string;
  material: string;
  capacity: string;
  moq: number;
  moqUnit: string;
  displayPriceText: string;
  samplePriceText: string;
  status: string;
  description: string;
  availabilityNote: string;
  features: string[];
  specifications: { label: string; value: string }[];
  images: string[];
  workingImageId: string | null;
  finishingOptions: FinishingOption[];
  supplier: SupplierInfo | null;
  reviews: ReviewItem[];
  leadTimeMinDays?: number;
  leadTimeMaxDays?: number;
  outerDiameterMm: string;
  innerDiameterMm: string;
  heightMm: string;
}

function nowISO(): string {
  return new Date().toISOString();
}

// ─── Job API base URL (absolute only — no relative fallback) ───

function requireApiBaseUrl(): string {
  const base = import.meta.env.VITE_API_BASE_URL
    .trim()
    .replace(/\/$/, '');

  if (!base) {
    throw new Error(
      'Missing VITE_API_BASE_URL. Finishing Job API requires an absolute GBCOSPACK API base URL (no relative /api fallback).',
    );
  }
  if (!/^https?:\/\//i.test(base)) {
    throw new Error(
      `VITE_API_BASE_URL must be an absolute http(s) URL (got "${base}"). Do not use relative /api paths.`,
    );
  }
  return base;
}

// ─── Job API Types ───

interface FinishingJobApi {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: {
    step: string | null;
    message: string | null;
    percent: number;
  };
  error: {
    code: string | null;
    message: string | null;
  } | null;
  result: {
    image_url?: string;
    result_id?: string;
  } | null;
  client_request_id: string | null;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
}

interface CreateFinishingJobResponse {
  ok: boolean;
  job: FinishingJobApi;
  request_id: string;
}

interface GetFinishingJobResponse {
  ok: boolean;
  job: FinishingJobApi;
  request_id: string;
}

// ─── Job metadata store (carries product context across create → poll) ───

const jobMetaStore = new Map<string, {
  product_id: string;
  working_image_id: string;
  structured_lines_json: Pick<FinishingLine, 'part_code' | 'finish_code' | 'color_code'>[];
  additional_request?: string | null;
  source?: string;
  request_locale?: string;
}>();

// ─── Map API job → FinishingPreviewJob ───

function mapApiJobToFinishingJob(
  apiJob: FinishingJobApi,
  meta?: {
    product_id: string;
    working_image_id: string;
    structured_lines_json: Pick<FinishingLine, 'part_code' | 'finish_code' | 'color_code'>[];
    additional_request?: string | null;
    source?: string;
    request_locale?: string;
  },
): FinishingPreviewJob {
  return {
    id: apiJob.id,
    product_id: meta?.product_id || '',
    working_image_id: meta?.working_image_id || '',
    status: apiJob.status as FinishingPreviewJob['status'],
    progress_step: apiJob.progress?.step || 'pending',
    progress_percent: apiJob.progress?.percent || 0,
    progress_message: apiJob.progress?.message || 'Preview request submitted. Waiting for worker.',
    structured_lines_json: meta?.structured_lines_json || [],
    additional_request: meta?.additional_request || null,
    source: meta?.source,
    request_locale: meta?.request_locale,
    result_id: apiJob.result?.result_id || null,
    result_image_url: apiJob.result?.image_url || (apiJob.status === 'completed' && apiJob.result?.image_url ? apiJob.result.image_url : null),
    error_code: apiJob.error?.code || null,
    error_message: apiJob.error?.message || null,
    created_at: apiJob.created_at,
    started_at: apiJob.started_at,
    completed_at: apiJob.completed_at,
  };
}

// ─── Structured lines sanitizer (BULLETPROOF) ───
// 1. Strips ALL "part-"/"method-" prefix variants (even nested ones like "part-part-01")
// 2. Drops ALL extra fields (part_name, finish_name, color_system, note, image_url)
// 3. Only keeps the 3 fields the worker needs: part_code, finish_code, color_code
function sanitizeStructuredLines(lines: FinishingLine[]): Pick<FinishingLine, 'part_code' | 'finish_code' | 'color_code'>[] {
  return lines.map((line) => ({
    part_code: cleanCode(line.part_code, 'part-'),
    finish_code: cleanCode(line.finish_code, 'method-'),
    color_code: line.color_code || '',
  }));
}

function cleanCode(value: string, prefix: string): string {
  if (!value) return '';
  let cleaned = value;
  // Aggressively strip all occurrences of the prefix (handles nested "part-part-01" → "01")
  while (cleaned.startsWith(prefix)) {
    cleaned = cleaned.slice(prefix.length);
  }
  // Also catch the other prefix type (e.g. a "part-01" value might also start with "method-")
  const otherPrefix = prefix === 'part-' ? 'method-' : 'part-';
  while (cleaned.startsWith(otherPrefix)) {
    cleaned = cleaned.slice(otherPrefix.length);
  }
  return cleaned;
}

// ─── Mock state (for fallback when Supabase auth is unavailable) ───

interface MockJob {
  id: string;
  data: FinishingPreviewJob;
  startTime: number;
}

const mockStore: Map<string, MockJob> = new Map();
let mockPollIntervals: Map<string, ReturnType<typeof setInterval>> = new Map();

const MOCK_STEPS = [
  { step: 'picked_by_worker', percent: 10, msg: 'Worker picked up the job.', delay: 1500 },
  { step: 'validating_request', percent: 20, msg: 'Validating request.', delay: 1200 },
  { step: 'building_prompt', percent: 30, msg: 'Building AI prompt.', delay: 2000 },
  { step: 'submitting_to_ai', percent: 40, msg: 'Submitting to AI service.', delay: 2000 },
  { step: 'ai_processing', percent: 60, msg: 'AI is generating the image.', delay: 5000 },
  { step: 'downloading_result', percent: 80, msg: 'Downloading result image.', delay: 2000 },
  { step: 'uploading_to_storage', percent: 85, msg: 'Uploading result image.', delay: 1500 },
  { step: 'saving_result', percent: 95, msg: 'Saving result.', delay: 1200 },
];

function startMockProgress(jobId: string): void {
  const entry = mockStore.get(jobId);
  if (!entry) return;

  entry.data.status = 'processing';
  entry.data.started_at = nowISO();

  let cumulativeMs = 0;

  MOCK_STEPS.forEach((stepDef, idx) => {
    cumulativeMs += stepDef.delay;
    setTimeout(() => {
      const current = mockStore.get(jobId);
      if (!current) return;

      current.data.progress_step = stepDef.step;
      current.data.progress_percent = stepDef.percent;
      current.data.progress_message = stepDef.msg;

      // Last step -> complete
      if (idx === MOCK_STEPS.length - 1) {
        const resultId = `mock-result-${jobId}`;
        current.data.status = 'completed';
        current.data.progress_step = 'completed';
        current.data.progress_percent = 100;
        current.data.progress_message = 'Preview completed.';
        current.data.result_id = resultId;
        current.data.completed_at = nowISO();
      }
    }, cumulativeMs);
  });
}

// ─── Service Functions ───

function hasSupabase(): boolean {
  if (!supabase) return false;
  const url = import.meta.env.VITE_PUBLIC_SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL || '';
  const key = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY || '';
  return url.length > 0 && key.length > 0;
}

// ─── Get Product Detail from Supabase ───
export async function getProductDetail(productId: string): Promise<ProductDetailData | null> {
  // When Supabase is not connected, return null
  if (!hasSupabase()) {
    return null;
  }

  try {
    // 1. Fetch product from DB
    const { data: productData, error: productError } = await supabase!
      .from('products')
      .select('*')
      .eq('id', productId)
      .maybeSingle();

    if (productError || !productData) {
      console.warn('[FinishingPreview] Product not found in DB:', productError?.message);
      return null;
    }

    const tags = (productData.tags as Record<string, unknown>) || {};

    // Resolve category display names from master_codes
    const categoryL1Code = (tags.category_l1_code as string) || '';
    const categoryL2Code = (tags.category_l2_code as string) || '';

    let categoryL1Name = '';
    let categoryL2Name = '';

    const codesToLookup: string[] = [];
    if (categoryL1Code) codesToLookup.push(categoryL1Code);
    if (categoryL2Code) codesToLookup.push(categoryL2Code);

    if (codesToLookup.length > 0) {
      try {
        const { data: masterData } = await supabase!
          .from('master_codes')
          .select('code, name')
          .eq('code_type', 'product_category')
          .in('code', codesToLookup);

        if (masterData) {
          for (const row of masterData) {
            if (row.code === categoryL1Code) categoryL1Name = row.name as string;
            if (row.code === categoryL2Code) categoryL2Name = row.name as string;
          }
        }
      } catch {
        // master_codes lookup failed — use raw codes as fallback
      }
    }

    // Fallback: use raw codes if names not found
    if (!categoryL1Name && categoryL1Code) categoryL1Name = categoryL1Code;
    if (!categoryL2Name && categoryL2Code) categoryL2Name = categoryL2Code;

    // 2. Fetch product images — working + web
    const { data: imagesData } = await supabase!
      .from('product_images')
      .select('*')
      .eq('product_id', productId)
      .in('image_role', ['working', 'web'])
      .order('image_role', { ascending: true })
      .order('sort_order', { ascending: true });

    // 3. Build image URLs: working first, then web
    const imageUrls: string[] = [];
    let workingImageId: string | null = null;
    const workingImages: typeof imagesData = [];
    const webImages: typeof imagesData = [];

    if (imagesData && imagesData.length > 0) {
      for (const img of imagesData) {
        if (img.image_role === 'working') {
          workingImages.push(img);
        } else if (img.image_role === 'web') {
          webImages.push(img);
        }
      }
    }

    // Use working image (intentionally 1024x1024) for display + AI input
    if (workingImages.length > 0) {
      workingImageId = workingImages[0].id as string;
    }

    // Merge: up to 1 working + up to 3 web images
    const selectedImages = [
      ...workingImages.slice(0, 1),
      ...webImages.slice(0, 3),
    ];

    // Resolve URLs from selected images
    for (const img of selectedImages) {
        let url: string | undefined;

        // Strategy 1: public_url from DB
        url = (img.public_url as string) || undefined;

        // Strategy 2: signed URL from Supabase Storage (private buckets)
        if (!url && img.storage_bucket && img.storage_path) {
          try {
            const { data: signedData, error: signedErr } = await supabase!
              .storage
              .from(img.storage_bucket as string)
              .createSignedUrl(img.storage_path as string, 3600);
            if (!signedErr && signedData?.signedUrl) {
              url = signedData.signedUrl;
            }
          } catch {
            // signed URL failed — try public URL next
          }
        }

        // Strategy 3: public URL fallback (if bucket is public)
        if (!url && img.storage_bucket && img.storage_path) {
          try {
            const { data: pubData } = supabase!
              .storage
              .from(img.storage_bucket as string)
              .getPublicUrl(img.storage_path as string);
            if (pubData?.publicUrl) url = pubData.publicUrl;
          } catch {
            // public URL also failed
          }
        }

        if (url) imageUrls.push(url);
      }

    // Strategy 4: fallback to external image URLs from product tags
    if (imageUrls.length === 0) {
      const tagImageUrl = (tags.image_url as string) || (tags.source_image_url as string) || '';
      if (tagImageUrl) imageUrls.push(tagImageUrl);
    }

    // 4. Parse material from tags.specs.material_outer
    const tagSpecs = (tags.specs as Record<string, unknown>) || {};
    const material = (tagSpecs.material_outer as string) || 'N/A';

    // 5. Parse capacity from tags.specs.capacity_ml
    const capacityMl = (tagSpecs.capacity_ml as string) || '';
    const capacity = capacityMl ? `${capacityMl}ml` : 'N/A';

    // 6. MOQ from products.moq column directly
    const moq = (productData.moq as number) || 0;

    // 7. Lead time from products.lead_time_min_days / lead_time_max_days
    const leadTimeMinDays = (productData.lead_time_min_days as number) || 0;
    const leadTimeMaxDays = (productData.lead_time_max_days as number) || 0;

    // 8. Build description (generate from name if DB has none)
    let description = (productData.description as string) || '';
    if (!description) {
      const productName = productData.name as string;
      description = `${productName} 제품입니다. 자세한 사양 및 가격 정보는 견적 요청을 통해 확인하실 수 있습니다.`;
    }

    // 9. Build specifications from tags.specs and product columns
    const specs: { label: string; value: string }[] = [];

    // SKU No.
    const sku = (productData.sku as string) || '';
    if (sku) {
      specs.push({ label: 'SKU No.', value: sku });
    }

    // 사이즈 (size_depth_mm x size_width_mm x size_height_mm)
    const sizeD = (tagSpecs.size_depth_mm as string) || '';
    const sizeW = (tagSpecs.size_width_mm as string) || '';
    const sizeH = (tagSpecs.size_height_mm as string) || '';
    if (sizeD || sizeW || sizeH) {
      const dims = [sizeD, sizeW, sizeH].filter(Boolean).join(' x ');
      specs.push({ label: '사이즈', value: `${dims} mm` });
    }

    // 용량 (ml)
    const capacityRaw = (tagSpecs.capacity_ml as string) || '';
    if (capacityRaw) {
      specs.push({ label: '용량 (ml)', value: capacityRaw });
    }

    // 무게 (g)
    const weightG = (tagSpecs.weight_g as string) || '';
    if (weightG) {
      specs.push({ label: '무게 (g)', value: weightG });
    }

    // 재질
    if (material !== 'N/A') {
      specs.push({ label: '재질', value: material });
    }

    // 최소주문수량(MOQ)
    if (moq > 0) {
      specs.push({ label: '최소주문수량(MOQ)', value: `${moq.toLocaleString()} pcs` });
    }

    // Stock 판매여부 (availability_status)
    const availStatus = (productData.availability_status as string) || '';
    if (availStatus) {
      const availMap: Record<string, string> = {
        'made to order': '주문생산',
        'made_to_order': '주문생산',
        'available': '재고판매',
        'in_stock': '재고판매',
        'discontinued': '단종',
      };
      specs.push({ label: 'Stock 판매여부', value: availMap[availStatus] || availStatus });
    }

    // 리드타임
    if (leadTimeMinDays > 0 || leadTimeMaxDays > 0) {
      const leadLabel = leadTimeMinDays > 0 && leadTimeMaxDays > 0
        ? `${leadTimeMinDays}~${leadTimeMaxDays}일`
        : leadTimeMinDays > 0 ? `${leadTimeMinDays}일` : `${leadTimeMaxDays}일`;
      specs.push({ label: '리드타임', value: leadLabel });
    }

    // 배송포장 방식
    const shippingPackaging = (tagSpecs.shipping_packaging as string) || '';
    if (shippingPackaging) {
      specs.push({ label: '배송포장 방식', value: shippingPackaging });
    }

    // 10. Features from tags.specs.notes — split by newline, max 4 items
    const notesRaw = (tagSpecs.notes as string) || '';
    const features: string[] = notesRaw
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line.length > 0)
      .slice(0, 4);

    // 10b. Extract compatibility specs for parts matching
    const outerDiameterMm = (tagSpecs.outer_diameter_mm as string) || '';
    const innerDiameterMm = (tagSpecs.inner_diameter_mm as string) || '';
    const heightMm = (tagSpecs.inner_diameter_mm as string) || '';

    return {
      id: productData.id as string,
      name: productData.name as string,
      sku: (productData.sku as string) || '',
      category: (productData.category as string) || '',
      categoryL1: categoryL1Name,
      categoryL2: categoryL2Name,
      material,
      capacity,
      moq,
      moqUnit: 'pcs',
      displayPriceText: (productData.display_price_text as string) || '',
      samplePriceText: (productData.sample_price_text as string) || '',
      status: (productData.status as string) || 'active',
      description,
      availabilityNote: (productData.availability_note as string) || '',
      features,
      specifications: specs,
      images: imageUrls,
      workingImageId,
      finishingOptions: [],
      supplier: null,
      reviews: [],
      leadTimeMinDays,
      leadTimeMaxDays,
      outerDiameterMm,
      innerDiameterMm,
      heightMm,
    };
  } catch (err) {
    console.error('[FinishingPreview] Failed to fetch product detail:', err);
    return null;
  }
}

export async function createPreviewJob(
  request: CreateJobRequest,
): Promise<FinishingPreviewJob> {
  if (!hasSupabase()) {
    return mockCreateJob(request);
  }

  // Try Job API first
  try {
    const { data: { session } } = await supabase!.auth.getSession();
    if (!session?.access_token) {
      console.warn('[FinishingPreview] No auth session, using mock fallback');
      return mockCreateJob(request);
    }

    // Resolve working_image_id from product_id if needed (UUID format check)
    let resolvedWorkingImageId = request.working_image_id;
    if (resolvedWorkingImageId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(resolvedWorkingImageId)) {
      const { data: images } = await supabase!
        .from('product_images')
        .select('id')
        .eq('product_id', request.product_id)
        .eq('image_role', 'working')
        .limit(1);

      if (images && images.length > 0) {
        resolvedWorkingImageId = images[0].id;
      } else {
        const { data: anyImages } = await supabase!
          .from('product_images')
          .select('id')
          .eq('product_id', request.product_id)
          .limit(1);

        if (anyImages && anyImages.length > 0) {
          resolvedWorkingImageId = anyImages[0].id;
        } else {
          resolvedWorkingImageId = '';
        }
      }
    }

    const sanitizedLines = sanitizeStructuredLines(request.structured_lines);
    const apiBase = requireApiBaseUrl();
    const jobId = crypto.randomUUID();
    const clientRequestId = `ui-finish-${jobId}`;

    // Store metadata for poll lookups
    const meta = {
      product_id: request.product_id,
      working_image_id: resolvedWorkingImageId || '',
      structured_lines_json: sanitizedLines,
      additional_request: request.additional_request || null,
      source: request.source || 'readdy_customer_ui',
      request_locale: request.request_locale || 'ko-KR',
    };
    jobMetaStore.set(jobId, meta);

    const res = await fetch(`${apiBase}/api/v1/jobs/finishing-preview`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: jobId,
        client_request_id: clientRequestId,
        product_id: request.product_id,
        working_image_id: resolvedWorkingImageId || null,
        structured_lines_json: sanitizedLines,
        additional_request: request.additional_request || undefined,
        request_locale: request.request_locale || 'ko-KR',
        source: request.source || 'readdy_customer_ui',
      }),
    });

    const responseText = await res.text().catch(() => '');
    let json: Record<string, unknown>;
    try {
      json = JSON.parse(responseText);
    } catch {
      console.error('[FinishingPreview] Non-JSON create response:', responseText.slice(0, 500));
      jobMetaStore.delete(jobId);
      return mockCreateJob(request);
    }

    if (!res.ok || !json.ok) {
      const errMsg = json?.error?.message ?? `Job create failed (${res.status})`;
      console.error('[FinishingPreview] Job API create failed:', errMsg);
      jobMetaStore.delete(jobId);
      return mockCreateJob(request);
    }

    const data = json as CreateFinishingJobResponse;
    // 202 = created, 200 = same client_request_id replay
    const createdJobId = data.job.id;

    // If the server returned a different job ID (idempotency replay), update meta key
    if (createdJobId !== jobId) {
      jobMetaStore.set(createdJobId, meta);
      jobMetaStore.delete(jobId);
    }

    const job = mapApiJobToFinishingJob(data.job, meta);

    // Note: Readdy does NOT call Kling API or any Edge Function for image generation.
    // The server worker handles Kling submission, Storage upload, and result persistence.
    // Readdy only creates the job via API and polls for status updates.

    return job;
  } catch (err) {
    console.error('[FinishingPreview] Job API error, falling back to mock:', err);
    return mockCreateJob(request);
  }
}

export async function pollJobStatus(jobId: string): Promise<FinishingPreviewJob> {
  // Check mock store first
  const mockEntry = mockStore.get(jobId);
  if (mockEntry) {
    return { ...mockEntry.data };
  }

  if (!hasSupabase()) {
    return createFailedJob(jobId, 'No Supabase connection');
  }

  try {
    const { data: { session } } = await supabase!.auth.getSession();
    if (!session?.access_token) {
      return createFailedJob(jobId, 'Authentication required');
    }

    const apiBase = requireApiBaseUrl();
    const res = await fetch(
      `${apiBase}/api/v1/jobs/finishing-preview/${jobId}`,
      {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      },
    );

    if (!res.ok) {
      if (res.status === 404 || res.status === 401) {
        return createFailedJob(jobId, res.status === 404 ? 'Job not found' : 'Authentication failed');
      }
      const responseText = await res.text().catch(() => '');
      console.error('[FinishingPreview] Poll HTTP error:', res.status, responseText.slice(0, 300));
      return createFailedJob(jobId, `Poll failed (HTTP ${res.status})`);
    }

    const responseText2 = await res.text().catch(() => '');
    let json: Record<string, unknown>;
    try {
      json = JSON.parse(responseText2);
    } catch {
      console.error('[FinishingPreview] Non-JSON poll response:', responseText2.slice(0, 300));
      return createFailedJob(jobId, `API 서버에서 예상치 못한 응답을 반환했습니다. 잠시 후 다시 시도해주세요.`);
    }

    if (!json.ok) {
      const errMsg = json?.error?.message ?? 'Poll failed';
      console.error('[FinishingPreview] Poll API error:', errMsg);
      return createFailedJob(jobId, errMsg);
    }

    const data = json as GetFinishingJobResponse;
    const meta = jobMetaStore.get(data.job.id) || jobMetaStore.get(jobId);
    return mapApiJobToFinishingJob(data.job, meta);
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : String(err);
    console.error('[FinishingPreview] Poll error:', errMsg);

    // If fetch itself fails (network/CORS/empty URL), fall back to mock
    if (errMsg.includes('Failed to fetch') || errMsg.includes('NetworkError')) {
      console.warn('[FinishingPreview] API unreachable, switching to mock fallback');
      const mockEntry = mockStore.get(jobId);
      if (mockEntry) {
        return { ...mockEntry.data };
      }
      return {
        id: jobId,
        product_id: '',
        working_image_id: '',
        status: 'processing',
        progress_step: 'pending',
        progress_percent: 0,
        progress_message: 'Waiting for server...',
        structured_lines_json: [],
        created_at: nowISO(),
        result_id: null,
        error_code: null,
        error_message: null,
        started_at: null,
        completed_at: null,
      } as FinishingPreviewJob;
    }

    return createFailedJob(jobId, 'Poll failed');
  }
}

export async function getPreviewResult(resultId: string): Promise<FinishingPreviewResult> {
  // Check if it's a mock result
  if (resultId.startsWith('mock-result-')) {
    const jobId = resultId.replace('mock-result-', '');
    const entry = mockStore.get(jobId);
    return {
      id: resultId,
      job_id: jobId,
      product_id: entry?.data.product_id || '',
      working_image_id: entry?.data.working_image_id || '',
      image_url: entry?.data.working_image_id || '', // Fallback: return working image
      result_status: 'completed',
      width: 1024,
      height: 1024,
      format: 'png',
    };
  }

  if (!hasSupabase()) {
    throw new Error('No Supabase connection');
  }

  try {
    const { data, error } = await supabase!
      .from('finishing_preview_results')
      .select('*')
      .eq('id', resultId)
      .single();

    if (error) throw error;

    // Try direct URL first (public_url or image_url)
    let imageUrl: string = (data.public_url as string) || (data.image_url as string) || '';

    // If no direct URL but storage info exists, generate a displayable URL.
    // The finishing-preview-results bucket is PRIVATE (public: false), so the
    // public URL (getPublicUrl) returns 403 in the browser. We MUST use a
    // signed URL (createSignedUrl) for display — the authenticated SELECT
    // policy on storage.objects already permits authenticated reads.
    if (!imageUrl && data.storage_bucket && data.storage_path) {
      // Prefer the signed URL (works for private buckets).
      try {
        const { data: signedData, error: signedError } = await supabase!
          .storage
          .from(data.storage_bucket as string)
          .createSignedUrl(data.storage_path as string, 3600);

        if (!signedError && signedData?.signedUrl) {
          imageUrl = signedData.signedUrl;
        } else {
          console.error('[FinishingPreview] Signed URL error:', signedError);
        }
      } catch (storageErr) {
        console.error('[FinishingPreview] Storage URL error:', storageErr);
      }

      // Fallback: public URL (only works if the bucket were ever made public).
      if (!imageUrl) {
        try {
          const { data: pubData } = supabase!
            .storage
            .from(data.storage_bucket as string)
            .getPublicUrl(data.storage_path as string);
          if (pubData?.publicUrl) {
            imageUrl = pubData.publicUrl;
          }
        } catch (pubErr) {
          console.error('[FinishingPreview] Public URL error:', pubErr);
        }
      }
    }

    return {
      id: data.id,
      job_id: data.job_id,
      product_id: data.product_id,
      working_image_id: data.working_image_id,
      image_url: imageUrl,
      public_url: data.public_url,
      width: data.width,
      height: data.height,
      format: data.format,
      result_status: data.result_status,
    };
  } catch (err) {
    console.error('[FinishingPreview] Result fetch error:', err);
    throw new Error('Failed to fetch preview result');
  }
}

// ─── Mock Implementation ───

async function mockCreateJob(request: CreateJobRequest): Promise<FinishingPreviewJob> {
  await new Promise((r) => setTimeout(r, 500));

  const jobId = `mock-job-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  const job: FinishingPreviewJob = {
    id: jobId,
    product_id: request.product_id,
    working_image_id: request.working_image_id,
    status: 'pending',
    progress_step: 'pending',
    progress_percent: 0,
    progress_message: 'Preview request submitted. Waiting for worker.',
    structured_lines_json: sanitizeStructuredLines(request.structured_lines),
    additional_request: request.additional_request || null,
    source: request.source || 'readdy_customer_ui',
    request_locale: request.request_locale || 'ko-KR',
    result_id: null,
    error_code: null,
    error_message: null,
    created_at: nowISO(),
    started_at: null,
    completed_at: null,
  };

  mockStore.set(jobId, { id: jobId, data: { ...job }, startTime: Date.now() });
  startMockProgress(jobId);

  return job;
}

function createFailedJob(jobId: string, errorMsg: string): FinishingPreviewJob {
  return {
    id: jobId,
    product_id: '',
    working_image_id: '',
    status: 'failed',
    progress_step: 'failed',
    progress_percent: 0,
    progress_message: errorMsg,
    structured_lines_json: [],
    error_code: 'SERVICE_ERROR',
    error_message: errorMsg,
    created_at: nowISO(),
  };
}